import { useQuery, useMutation } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { Card, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import { useNavigate, useBlocker } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faBuilding,
  faMapMarkerAlt,
  faInfoCircle,
  faCircleNotch,
  faUser,
  faCheckCircle,
  faSearch,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "providers/ToastProvider";
import { usePermissionModal } from "contexts/PermissionModalContext";
import {
  CriarFornecedorDtoType,
  EnderecoType,
  TipoPessoa,
} from "data/fornecedores";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import { EmpresaBrasilApi } from "data/brasilApi";
import { MunicipioType } from "data/municipio";
import MunicipioSelector from "components/common/MunicipioSelector";
import { CepViaCepApi } from "data/viaCepApi";

// Define os itens do breadcrumb
const fornecedorBreadcrumbItems = () => [
  {
    label: "Cadastros",
    url: "/cadastros/fornecedores",
  },
  {
    label: "Fornecedores",
    url: "/cadastros/fornecedores",
  },
  {
    label: "Novo",
    active: true,
  },
];

// Função para buscar dados do fornecedor através do CNPJ
const fetchEmpresa = async ({ queryKey }: any) => {
  const [, cnpj] = queryKey;
  const cnpjLimpo = cnpj.replace(/\D/g, "");
  if (!cnpjLimpo || cnpjLimpo.length !== 14) return null;

  try {
    const { data } = await axios.get(
      `https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`,
      {
        withCredentials: false,
      }
    );
    return data;
  } catch (error) {
    // Capturar a mensagem de erro para exibição no toast
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(
      "Não foi possível consultar o CNPJ. Verifique o número informado."
    );
  }
};

// Função para buscar dados de endereço através do CEP
const fetchCep = async ({ queryKey }: any) => {
  const [, cep] = queryKey;
  const cepLimpo = cep.replace(/\D/g, "");
  if (!cepLimpo || cepLimpo.length !== 8) return null;

  try {
    const { data } = await axios.get(
      `https://viacep.com.br/ws/${cepLimpo}/json/`,
      {
        withCredentials: false,
      }
    );

    // ViaCEP retorna objeto com erro se CEP não existir
    if (data.erro) {
      throw new Error("CEP não encontrado");
    }

    return data;
  } catch (error) {
    // Capturar a mensagem de erro para exibição no toast
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(
      "Não foi possível consultar o CEP. Verifique o número informado."
    );
  }
};

// Função para buscar município por código IBGE
const fetchMunicipioByIbge = async ({ queryKey }: any) => {
  const [, codigoIbge] = queryKey;
  if (!codigoIbge) return null;

  try {
    const { data } = await axios.get(`/api/municipio/ibge/${codigoIbge}`);

    // Verificar se a resposta foi bem-sucedida
    if (!data.isSuccess) {
      throw new Error(data.message || "Município não encontrado.");
    }

    // Retornar os dados do município
    return data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Se a API retornou um erro estruturado
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      // Se é um erro 404, pode ser que o município não existe
      if (error.response?.status === 404) {
        throw new Error("Município não encontrado para o código IBGE informado.");
      }
    }
    throw new Error("Não foi possível encontrar o município.");
  }
};

const NovoFornecedor = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getMutationErrorHandler } = usePermissionModal();
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [formInitialized, setFormInitialized] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [tipoPessoaSelecionado, setTipoPessoaSelecionado] =
    useState<TipoPessoa>(TipoPessoa.Juridica);
  const [showMunicipioSelector, setShowMunicipioSelector] = useState(false);

  // Configurar o formulário usando react-hook-form
  const form = useForm<CriarFornecedorDtoType>({
    mode: "onSubmit", // Validar apenas no submit
    defaultValues: {
      nome: "",
      nomeFantasia: "",
      razaoSocial: "",
      cnpj: "",
      cpf: "",
      inscricaoEstadual: "",
      telefone: "",
      celular: "",
      email: undefined, // undefined para que validação só ocorra se houver valor
      dataNascimento: "",
      endereco: {
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        municipioId: 0,
        municipio: "",
        uf: "",
      },
      tipoPessoa: TipoPessoa.Juridica,
      isCliente: false,
      isFornecedorProduto: true, // Por padrão, marcaremos como fornecedor de produto
      prazoEntrega: undefined, // Campo para prazo de entrega (em dias)
      valorMinimo: undefined, // Campo para valor mínimo para compra
    },
  });

  // Query para buscar dados do fornecedor pelo CNPJ
  const cnpjWatch = form.watch("cnpj");
  const cnpjLimpo = cnpjWatch ? cnpjWatch.replace(/\D/g, "") : "";
  // Observar o estado do checkbox "Fornecedor de Produto" para controlar campos de informações de compra
  const isFornecedorProduto = form.watch("isFornecedorProduto");

  const dadosEmpresaQuery = useQuery<EmpresaBrasilApi>({
    queryKey: ["fornecedor", cnpjLimpo],
    queryFn: fetchEmpresa,
    enabled: !!cnpjLimpo && cnpjLimpo.length === 14,
    // Evitar refetch automático em segundo plano
    staleTime: Infinity,
    // Só refetch quando explicitamente solicitado
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Estado para controlar os campos preenchidos automaticamente
  const [fieldsPopulated, setFieldsPopulated] = useState<
    Record<string, boolean>
  >({});

  // Estado para rastrear o último CNPJ processado com sucesso
  const [lastProcessedCnpj, setLastProcessedCnpj] = useState<string | null>(
    null
  );
  // Estado para controlar se a consulta CNPJ foi iniciada pelo usuário
  const [cnpjConsultaIniciada, setCnpjConsultaIniciada] =
    useState<boolean>(false);

  // Estado para rastrear o código IBGE obtido da API
  const [codigoIbgeAtual, setCodigoIbgeAtual] = useState<string | null>(null);

  // Query para buscar município por código IBGE
  const municipioIbgeQuery = useQuery<MunicipioType>({
    queryKey: ["municipio-ibge", codigoIbgeAtual],
    queryFn: fetchMunicipioByIbge,
    enabled: !!codigoIbgeAtual,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Tratar erros na consulta de CNPJ
  useEffect(() => {
    if (dadosEmpresaQuery.isError) {
      // Extrair mensagem de erro de forma simplificada
      const errorMessage =
        dadosEmpresaQuery.error instanceof Error
          ? dadosEmpresaQuery.error.message
          : "Erro ao consultar CNPJ. Verifique o número informado ou preencha os dados manualmente.";

      showToast(
        <>
          <strong>Erro ao consultar CNPJ</strong>
          <p className="mb-0 mt-1">{errorMessage}</p>
        </>,
        "error"
      );

      // Resetar o estado de consulta iniciada
      setCnpjConsultaIniciada(false);
    }
  }, [dadosEmpresaQuery.isError]);

  // Preencher formulário com dados obtidos da API quando disponíveis
  useEffect(() => {
    const { data } = dadosEmpresaQuery;
    // Resetar o estado de consulta iniciada quando a consulta for concluída com sucesso
    if (dadosEmpresaQuery.isSuccess || dadosEmpresaQuery.isError) {
      setCnpjConsultaIniciada(false);
    }

    if (!data || mutation.isError) return;

    // Obter o CNPJ atual (limpo)
    const currentCnpj = form.getValues().cnpj?.replace(/\D/g, "") || "";

    // Verificar se já processamos este CNPJ específico
    if (lastProcessedCnpj === currentCnpj) return;

    // Limpar estado de campos preenchidos
    setFieldsPopulated({});

    // Função para preencher um campo com efeito visual
    const fillFieldWithEffect = (
      field: any,
      value: string,
      delay: number = 0
    ) => {
      setTimeout(() => {
        form.setValue(field as any, value, { shouldDirty: true });
        setFieldsPopulated((prev) => ({ ...prev, [field]: true }));

        // Remover efeito após 2 segundos
        setTimeout(() => {
          setFieldsPopulated((prev) => ({ ...prev, [field]: false }));
        }, 2000);
      }, delay);
    };

    // Sequência de preenchimento dos campos com efeitos visuais
    fillFieldWithEffect("razaoSocial", data.razao_social || "", 100);
    fillFieldWithEffect("nomeFantasia", data.nome_fantasia || "", 200);
    // Para pessoa jurídica, não preenchemos mais o campo "nome"
    fillFieldWithEffect(
      "telefone",
      formatTelefone(data.ddd_telefone_1 || ""),
      300
    );
    // Só preencher email se houver valor válido
    if (data.email && data.email.trim()) {
      fillFieldWithEffect("email", data.email, 500);
    }

    // Preencher dados de endereço (exceto município e UF que agora são selecionados pelo componente)
    fillFieldWithEffect("endereco.cep", formatCep(data.cep || ""), 600);
    fillFieldWithEffect("endereco.logradouro", data.logradouro || "", 700);
    fillFieldWithEffect("endereco.numero", data.numero || "", 800);
    fillFieldWithEffect("endereco.complemento", data.complemento || "", 900);
    fillFieldWithEffect("endereco.bairro", data.bairro || "", 1000);

    // Buscar município automaticamente usando código IBGE
    if (data.codigo_municipio_ibge) {
      // Definir o código IBGE para disparar a query do município
      setTimeout(() => {
        setCodigoIbgeAtual(data.codigo_municipio_ibge);
      }, 1100); // Atraso para dar tempo dos outros campos serem preenchidos primeiro
    } else if (data.cep) {
      // Se não tiver código IBGE direto do CNPJ, usar o CEP para buscar na ViaCEP
      setTimeout(async () => {
        try {
          const cepLimpo = data.cep.replace(/\D/g, "");
          if (cepLimpo.length === 8) {
            const response = await axios.get(
              `https://viacep.com.br/ws/${cepLimpo}/json/`,
              { withCredentials: false }
            );

            if (response.data && !response.data.erro && response.data.ibge) {
              setCodigoIbgeAtual(response.data.ibge);
            }
          }
        } catch (error) {
          console.log("Erro ao buscar IBGE pelo CEP:", error);
          // Se não conseguir buscar por CEP, não fazemos nada adicional
        }
      }, 1200);
    }

    // Atualizar o estado do formulário
    setFormDirty(true);

    // Registrar que processamos este CNPJ
    setLastProcessedCnpj(currentCnpj);

    // Mostrar um toast de sucesso
    showToast("Dados do fornecedor carregados com sucesso!", "success");
  }, [dadosEmpresaQuery.data, dadosEmpresaQuery.dataUpdatedAt]);

  // Sincronizar tipoPessoaSelecionado com o valor do formulário
  useEffect(() => {
    form.setValue("tipoPessoa", tipoPessoaSelecionado, { shouldDirty: true });
  }, [tipoPessoaSelecionado]);

  // Marcar formulário como inicializado após o primeiro render
  useEffect(() => {
    // Inicializa o formulário e garante que comece como "limpo"
    form.reset(form.getValues(), {
      keepDirty: false,
      keepTouched: false,
      keepIsValid: true,
      keepIsSubmitted: false,
      keepSubmitCount: false,
    });
    setFormDirty(false);
    shouldBlockNavigationRef.current = true; // Enable blocking after initialization
    setFormInitialized(true);
  }, []);

  // Rastrear mudanças no formulário
  useEffect(() => {
    if (!formInitialized) return;

    // Observar todas as mudanças no formulário
    const subscription = form.watch(() => {
      if (!formDirty) {
        setFormDirty(true);
      }
    });

    return () => {
      if (typeof subscription === "function") {
        // subscription();
      } else if (
        subscription &&
        typeof subscription.unsubscribe === "function"
      ) {
        subscription.unsubscribe();
      }
    };
  }, [form, formDirty, formInitialized]);

  // Reference to track whether form is dirty and if navigation should be blocked
  const isDirtyRef = useRef(false);
  const shouldBlockNavigationRef = useRef(true);

  useEffect(() => {
    isDirtyRef.current = form.formState.isDirty || formDirty;
  }, [form.formState.isDirty, formDirty]);

  // Configurar bloqueador de navegação
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if form is not initialized yet
    if (!formInitialized) return false;

    // Don't block if we're submitting the form or if it was already successful
    if (
      form.formState.isSubmitting ||
      (mutation.isSuccess && mutation.data?.isSuccess) ||
      !shouldBlockNavigationRef.current
    ) {
      return false;
    }

    // Check for real changes in the form
    const hasRealChanges = Object.keys(form.formState.dirtyFields).length > 0;

    const shouldBlock =
      (hasRealChanges || formDirty) &&
      currentLocation.pathname !== nextLocation.pathname;

    if (shouldBlock) {
      setPendingNavigation(nextLocation.pathname);
      setShowNavigationModal(true);
    }

    return shouldBlock;
  });

  // Manipuladores para o modal de confirmação de navegação
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      // Reset dirty state and disable blocking before navigating
      isDirtyRef.current = false;
      shouldBlockNavigationRef.current = false;

      // Unblock and navigate
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    blocker.reset?.();
  };

  // Configurar efeito para mostrar modal quando o bloqueador é ativado
  useEffect(() => {
    if (blocker.state === "blocked") {
      setPendingNavigation(blocker.location.pathname);
      setShowNavigationModal(true);
      return;
    }

    if (blocker.state === "proceeding") {
      setShowNavigationModal(false);
      setPendingNavigation(null);
    }
  }, [blocker]);

  // Função para formatar CNPJ
  const formatCnpj = (value: string) => {
    if (!value) return value;
    const cnpjNumbers = value.replace(/\D/g, "");
    if (cnpjNumbers.length <= 14) {
      // Se tiver todos os 14 dígitos, aplica a máscara completa
      if (cnpjNumbers.length === 14) {
        return cnpjNumbers.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          "$1.$2.$3/$4-$5"
        );
      }
      // Aplica formatação parcial conforme os dígitos disponíveis
      return cnpjNumbers
        .replace(/^(\d{2})/, "$1.")
        .replace(/^(\d{2})\.(\d{3})/, "$1.$2.")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})/, "$1.$2.$3/")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})/, "$1.$2.$3/$4-");
    }
    return cnpjNumbers.substring(0, 14);
  };

  // Função para formatar CPF
  const formatCpf = (value: string) => {
    if (!value) return value;
    const cpfNumbers = value.replace(/\D/g, "");
    if (cpfNumbers.length <= 11) {
      // Se tiver todos os 11 dígitos, aplica a máscara completa
      if (cpfNumbers.length === 11) {
        return cpfNumbers.replace(
          /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
          "$1.$2.$3-$4"
        );
      }
      // Aplica formatação parcial conforme os dígitos disponíveis
      return cpfNumbers
        .replace(/^(\d{3})/, "$1.")
        .replace(/^(\d{3})\.(\d{3})/, "$1.$2.")
        .replace(/^(\d{3})\.(\d{3})\.(\d{3})/, "$1.$2.$3-");
    }
    return cpfNumbers.substring(0, 11);
  };

  // Função para formatar telefone
  const formatTelefone = (value: string) => {
    if (!value) return value;
    const telefoneNumbers = value.replace(/\D/g, "");
    if (telefoneNumbers.length <= 11) {
      // Celular com 11 dígitos
      if (telefoneNumbers.length === 11) {
        return telefoneNumbers.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
      }
      // Telefone fixo com 10 dígitos
      else if (telefoneNumbers.length === 10) {
        return telefoneNumbers.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
      }
      // Formatação parcial
      return telefoneNumbers
        .replace(/^(\d{2})/, "($1) ")
        .replace(/^(\d{2})\s(\d{4,5})/, "($1) $2-");
    }
    return telefoneNumbers.substring(0, 11);
  };

  // Mutação para criar um novo fornecedor
  const mutation = useMutation<any, any, CriarFornecedorDtoType>({
    mutationFn: async (data: CriarFornecedorDtoType) => {
      // Prepara os dados de acordo com o tipo de pessoa
      let fornecedorData: any = {
        ...data,
        telefone: data.telefone.replace(/\D/g, ""),
        celular: data.celular ? data.celular.replace(/\D/g, "") : undefined,
        dataNascimento: data.dataNascimento || undefined,
        endereco: {
          cep: data.endereco.cep.replace(/\D/g, ""),
          logradouro: data.endereco.logradouro,
          numero: data.endereco.numero,
          complemento: data.endereco.complemento,
          bairro: data.endereco.bairro,
          municipioId: data.endereco.municipioId,
          // Não enviar municipio e uf, apenas municipioId
        },
      };

      // Adicionar campos específicos com base no tipo de pessoa
      if (data.tipoPessoa === TipoPessoa.Juridica) {
        fornecedorData.cnpj = data.cnpj
          ? data.cnpj.replace(/\D/g, "")
          : undefined;
        fornecedorData.razaoSocial = data.razaoSocial;
      } else if (data.tipoPessoa === TipoPessoa.Fisica) {
        fornecedorData.cpf = data.cpf ? data.cpf.replace(/\D/g, "") : undefined;
      }

      const response = await axios.post("/api/fornecedor", fornecedorData);
      return response.data;
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para cadastrar fornecedor",
      (error) => {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<any>;
          if (axiosError.response?.data?.errors) {
            // Mapear erros do backend para os campos do formulário
            const errors = axiosError.response.data.errors;

            // Limpar erros anteriores antes de setar novos
            form.clearErrors();

            // Coletar todas as mensagens de erro para exibir no toast
            let errorMessages: string[] = [];

            Object.keys(errors).forEach((key) => {
              // Verificar se é um erro para um campo de endereço
              if (key.startsWith("Endereco.")) {
                const enderecoField = key
                  .replace("Endereco.", "")
                  .toLowerCase();
                form.setError(`endereco.${enderecoField}` as any, {
                  type: "manual",
                  message: errors[key],
                });
                errorMessages.push(`${enderecoField}: ${errors[key]}`);
              } else {
                // Campo normal
                const keyLower = key.toLowerCase();
                form.setError(keyLower as any, {
                  type: "manual",
                  message: errors[key],
                });
                errorMessages.push(`${key}: ${errors[key]}`);
              }
            });

            // Exibir mensagens de erro no toast apenas se houver mensagens
            if (errorMessages.length > 0) {
              showToast(
                <>
                  <strong>Erro ao cadastrar fornecedor</strong>
                  <ul className="mb-0 mt-1 ps-3">
                    {errorMessages.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </>,
                "error"
              );
            }

            // Reset da mutation para permitir nova tentativa
            mutation.reset();
          } else {
            // Usar toast em vez de setGeneralError para mensagens gerais
            showToast(
              <>
                <strong>Erro ao cadastrar fornecedor</strong>
                <p className="mb-0 mt-1">
                  {axiosError.response?.data?.message ||
                    "Verifique os dados e tente novamente."}
                </p>
              </>,
              "error"
            );

            // Reset da mutation para permitir nova tentativa
            mutation.reset();
          }
        }
      }
    ),
    onSuccess: (response) => {

      // Disable navigation blocking immediately
      shouldBlockNavigationRef.current = false;

      // Reset form dirty state and clear any previous errors
      form.reset(form.getValues(), { keepValues: true });
      setFormDirty(false);
      isDirtyRef.current = false;

      console.log("Fornecedor cadastrado com sucesso:", response.data);
      console.log("Redirecionando para a lista de fornecedores...", formDirty);
      console.log("Form dirty state:", isDirtyRef);

      // Navigate immediately since we disabled blocking
      navigate("/cadastros/fornecedores");

      showToast(
        <>
          Fornecedor <strong>{response?.codigo}</strong> cadastrado com
          sucesso!
        </>,
        "success"
      );
    },
  });

  useEffect(() => {
    console.log("Mutation status changed:", mutation.status);
    if (mutation.isSuccess && mutation.isSuccess) {
      console.log("Mutation was successful:", mutation);
      setFormDirty(false);
      shouldBlockNavigationRef.current = false;
    }
  }, [mutation.isSuccess]);

  // Função para validar campos conforme o tipo de pessoa
  const validarCamposPorTipoPessoa = (
    data: CriarFornecedorDtoType
  ): boolean => {
    let isValid = true;

    // Validar campo "nome" apenas para Pessoa Física
    if (data.tipoPessoa === TipoPessoa.Fisica && !data.nome?.trim()) {
      form.setError("nome", {
        type: "manual",
        message: "Nome é obrigatório",
      });
      isValid = false;
    }

    if (data.tipoPessoa === TipoPessoa.Juridica) {
      // Validar campos específicos de pessoa jurídica
      if (!data.razaoSocial?.trim()) {
        form.setError("razaoSocial", {
          type: "manual",
          message: "Razão Social é obrigatória para Pessoa Jurídica",
        });
        isValid = false;
      }

      const cnpjLimpo = data.cnpj?.replace(/\D/g, "") || "";
      if (cnpjLimpo.length !== 14) {
        form.setError("cnpj", {
          type: "manual",
          message: "CNPJ deve conter 14 dígitos",
        });
        isValid = false;
      }
    } else if (data.tipoPessoa === TipoPessoa.Fisica) {
      // Validar campos específicos de pessoa física
      const cpfLimpo = data.cpf?.replace(/\D/g, "") || "";
      if (cpfLimpo.length !== 11) {
        form.setError("cpf", {
          type: "manual",
          message: "CPF deve conter 11 dígitos",
        });
        isValid = false;
      }

      // Não validar razão social para pessoa física, pois é opcional
    }

    if (!isValid) {
      showToast(
        <>
          <strong>Erro de validação</strong>
          <p className="mb-0 mt-1">
            Verifique os campos destacados e tente novamente.
          </p>
        </>,
        "error"
      );
    }

    return isValid;
  };

  // Handler para submissão do formulário
  const onSubmit: SubmitHandler<CriarFornecedorDtoType> = (data) => {
    // Limpar erros anteriores ao tentar submeter novamente
    form.clearErrors();

    // Validar campos baseado no tipo de pessoa
    if (!validarCamposPorTipoPessoa(data)) {
      return;
    }

    // Validar se o CEP tem o formato correto
    const cepLimpo = data.endereco.cep.replace(/\D/g, "");
    if (cepLimpo.length !== 8) {
      form.setError("endereco.cep", {
        type: "manual",
        message: "CEP deve conter 8 dígitos",
      });
      showToast(
        <>
          <strong>Erro de validação</strong>
          <p className="mb-0 mt-1">CEP deve conter 8 dígitos.</p>
        </>,
        "error"
      );
      return;
    }

    // Verificar se o município foi selecionado
    if (!data.endereco.municipioId || data.endereco.municipioId <= 0) {
      showToast(
        <>
          <strong>Erro de validação</strong>
          <p className="mb-0 mt-1">Você precisa selecionar um município.</p>
        </>,
        "error"
      );
      form.setError("endereco.municipio", {
        type: "manual",
        message: "Município é obrigatório",
      });
      return;
    }

    // Validar se o telefone tem um formato válido
    const telefoneLimpo = data.telefone.replace(/\D/g, "");
    if (telefoneLimpo.length < 10 || telefoneLimpo.length > 11) {
      form.setError("telefone", {
        type: "manual",
        message: "Telefone deve conter entre 10 e 11 dígitos",
      });
      showToast(
        <>
          <strong>Erro de validação</strong>
          <p className="mb-0 mt-1">
            Telefone deve conter entre 10 e 11 dígitos.
          </p>
        </>,
        "error"
      );
      return;
    }

    // Se informou celular, validar
    if (data.celular) {
      const celularLimpo = data.celular.replace(/\D/g, "");
      if (
        celularLimpo.length > 0 &&
        (celularLimpo.length < 10 || celularLimpo.length > 11)
      ) {
        form.setError("celular", {
          type: "manual",
          message: "Celular deve conter entre 10 e 11 dígitos",
        });
        showToast(
          <>
            <strong>Erro de validação</strong>
            <p className="mb-0 mt-1">
              Celular deve conter entre 10 e 11 dígitos.
            </p>
          </>,
          "error"
        );
        return;
      }
    }

    // Não limpar o estado antes de enviar - apenas enviar os dados
    mutation.mutate(data);
  };

  // Função para formatar CEP
  const formatCep = (value: string) => {
    if (!value) return value;
    const cepNumbers = value.replace(/\D/g, "");
    if (cepNumbers.length <= 8) {
      // Se tiver todos os 8 dígitos, aplica a máscara completa
      if (cepNumbers.length === 8) {
        return cepNumbers.replace(/^(\d{5})(\d{3})$/, "$1-$2");
      }
      // Formatação parcial
      return cepNumbers.replace(/^(\d{5})/, "$1-");
    }
    return cepNumbers.substring(0, 8);
  };

  // Query para buscar dados de endereço pelo CEP
  const cepWatch = form.watch("endereco.cep");
  const cepLimpo = cepWatch ? cepWatch.replace(/\D/g, "") : "";

  const dadosCepQuery = useQuery<CepViaCepApi>({
    queryKey: ["cep", cepLimpo],
    queryFn: fetchCep,
    enabled: false, // Não executar automaticamente, apenas sob demanda
    // Evitar refetch automático em segundo plano
    staleTime: Infinity,
    // Só refetch quando explicitamente solicitado
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Estado para rastrear o último CEP processado com sucesso
  const [lastProcessedCep, setLastProcessedCep] = useState<string | null>(null);
  // Estado para controlar a exibição de mensagem de CEP válido
  const [showCepSuccess, setShowCepSuccess] = useState(false);
  // Estado para controlar se a consulta CEP foi iniciada pelo usuário
  const [cepConsultaIniciada, setCepConsultaIniciada] =
    useState<boolean>(false);

  // Tratar erros na consulta de CEP
  useEffect(() => {
    if (dadosCepQuery.isError) {
      const errorMessage =
        dadosCepQuery.error instanceof Error
          ? dadosCepQuery.error.message
          : "Erro ao consultar CEP. Verifique o número informado ou preencha o endereço manualmente.";

      showToast(
        <>
          <strong>Erro ao consultar CEP</strong>
          <p className="mb-0 mt-1">{errorMessage}</p>
        </>,
        "error"
      );

      // Resetar o estado de consulta iniciada
      setCepConsultaIniciada(false);
    }

    // Resetar o estado de consulta iniciada quando a consulta for concluída com sucesso
    if (dadosCepQuery.isSuccess) {
      setCepConsultaIniciada(false);
    }
  }, [dadosCepQuery.error, dadosCepQuery.isError, dadosCepQuery.isSuccess]);

  // Consulta CEP usando a Brasil API
  const consultarCep = async (cep: string) => {
    try {
      const cepLimpo = cep.replace(/\D/g, "");
      if (cepLimpo.length !== 8) return;

      // Limpar qualquer erro anterior
      form.clearErrors("endereco.cep");

      // Disparar a consulta manualmente
      const result = await dadosCepQuery.refetch();

      // Verificar se temos dados válidos
      if (!result.data || result.isError) {
        // Não precisamos mostrar o toast aqui, pois o useEffect para dadosCepQuery.isError já vai fazer isso

        // Apenas adicionamos o erro no campo para destacar visualmente
        form.setError("endereco.cep", {
          type: "manual",
          message: "CEP não encontrado",
        });
        return;
      }

      const enderecoDados = result.data;

      // Verificar se há dados de endereço válidos
      if (!enderecoDados.logradouro && !enderecoDados.localidade) {
        // Mostrar mensagem no toast em vez de definir erro no formulário
        showToast(
          "CEP encontrado, mas sem dados de endereço completos.",
          "warning"
        );

        // Ainda adicionamos o erro no campo para destacar visualmente
        form.setError("endereco.cep", {
          type: "manual",
          message: "CEP encontrado, mas sem dados de endereço",
        });
        return;
      }

      // Preencher campos do endereço usando ViaCEP
      form.setValue("endereco.logradouro", enderecoDados.logradouro || "", {
        shouldDirty: true,
      });
      form.setValue("endereco.bairro", enderecoDados.bairro || "", {
        shouldDirty: true,
      });

      // Marcar como último CEP processado
      setLastProcessedCep(cepLimpo);

      // Adicionar efeito de highlight aos campos preenchidos
      setFieldsPopulated((prev) => ({
        ...prev,
        "endereco.logradouro": true,
        "endereco.bairro": true,
      }));

      // Remover efeito após 2 segundos
      setTimeout(() => {
        setFieldsPopulated((prev) => ({
          ...prev,
          "endereco.logradouro": false,
          "endereco.bairro": false,
        }));
      }, 2000);

      // Focar no próximo campo
      if (enderecoDados.logradouro) {
        // Se o logradouro foi preenchido, vamos focar no campo de número
        document.getElementById("numero")?.focus();
      } else {
        // Se não, vamos focar no campo de logradouro para que o usuário preencha manualmente
        document.getElementById("logradouro")?.focus();
      }

      // Mostrar toast de sucesso
      showToast(
        <>
          <div>CEP encontrado com sucesso!</div>
          <div className="mt-1 small">
            Buscando município automaticamente...
          </div>
        </>,
        "success"
      );

      // Buscar município automaticamente usando código IBGE do ViaCEP
      if (enderecoDados.ibge) {
        // Usar o código IBGE para buscar o município automaticamente
        setTimeout(() => {
          setCodigoIbgeAtual(enderecoDados.ibge);
        }, 500);
      } else if (enderecoDados.localidade && enderecoDados.uf) {
        // Fallback: se não tiver IBGE, abrir seletor manual
        setTimeout(() => {
          setShowMunicipioSelector(true);
        }, 500);
      }

      // Mostrar indicador de CEP válido
      setShowCepSuccess(true);
    } catch (error) {
      // Não precisamos mostrar o toast aqui, pois o useEffect para dadosCepQuery.isError já vai fazer isso

      // Adicionamos o erro no campo para destacar visualmente
      form.setError("endereco.cep", {
        type: "manual",
        message: "Erro ao consultar o CEP",
      });

      // Resetar estado de sucesso
      setShowCepSuccess(false);
    }
  };

  // Estado para controlar a exibição do sucesso da busca CNPJ
  const [showCnpjSuccess, setShowCnpjSuccess] = useState(false);

  // Função para lidar com a seleção de município
  const handleMunicipioSelect = (municipio: MunicipioType) => {
    // Preencher apenas o municipioId
    form.setValue("endereco.municipioId", municipio.id, { shouldDirty: true });

    // Também manter o nome do município para exibição
    form.setValue("endereco.municipio", municipio.nome, { shouldDirty: true });
    form.setValue("endereco.uf", municipio.uf, { shouldDirty: true });

    // Adicionar efeito de highlight aos campos preenchidos
    setFieldsPopulated((prev) => ({
      ...prev,
      "endereco.municipio": true,
      "endereco.uf": true,
    }));

    // Remover efeito após 2 segundos
    setTimeout(() => {
      setFieldsPopulated((prev) => ({
        ...prev,
        "endereco.municipio": false,
        "endereco.uf": false,
      }));
    }, 2000);

    // Mostrar toast de sucesso
    showToast(
      <>
        Município{" "}
        <strong>
          {municipio.nome}/{municipio.uf}
        </strong>{" "}
        selecionado com sucesso!
      </>,
      "success"
    );

    // Fechar o modal
    setShowMunicipioSelector(false);

    // Atualizar estado do formulário
    setFormDirty(true);
  };

  // Efeito para mostrar a mensagem de sucesso
  useEffect(() => {
    // Precisamos verificar se houve uma mudança real nos dados
    const cnpjAtual = form.getValues().cnpj?.replace(/\D/g, "") || "";
    if (
      dadosEmpresaQuery.isSuccess &&
      dadosEmpresaQuery.data &&
      lastProcessedCnpj === cnpjAtual
    ) {
      setShowCnpjSuccess(true);

      // Esconder após 5 segundos
      const timer = setTimeout(() => {
        setShowCnpjSuccess(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [lastProcessedCnpj]);

  // Processar dados do município quando a query retornar
  useEffect(() => {
    if (municipioIbgeQuery.isSuccess && municipioIbgeQuery.data && codigoIbgeAtual) {
      const municipio = municipioIbgeQuery.data;

      // Preencher os campos do município
      form.setValue("endereco.municipioId", municipio.id, { shouldDirty: true });
      form.setValue("endereco.municipio", municipio.nome, { shouldDirty: true });
      form.setValue("endereco.uf", municipio.uf, { shouldDirty: true });

      // Adicionar efeito de highlight aos campos preenchidos
      setFieldsPopulated((prev) => ({
        ...prev,
        "endereco.municipio": true,
        "endereco.uf": true,
      }));

      // Remover efeito após 2 segundos
      setTimeout(() => {
        setFieldsPopulated((prev) => ({
          ...prev,
          "endereco.municipio": false,
          "endereco.uf": false,
        }));
      }, 2000);

      // Mostrar toast de sucesso
      showToast(
        <>
          Município{" "}
          <strong>
            {municipio.nome}/{municipio.uf}
          </strong>{" "}
          identificado automaticamente!
        </>,
        "success"
      );

      // Limpar o código IBGE atual
      setCodigoIbgeAtual(null);
    } else if (municipioIbgeQuery.isError && codigoIbgeAtual) {
      // Se não conseguir encontrar o município, sugerir seleção manual
      showToast(
        <>
          <div>Município não encontrado automaticamente.</div>
          <div className="mt-1 small">
            Por favor, selecione o município manualmente.
          </div>
        </>,
        "warning"
      );

      // Abrir o seletor de município
      setTimeout(() => {
        setShowMunicipioSelector(true);
      }, 500);

      // Limpar o código IBGE atual
      setCodigoIbgeAtual(null);
    }
  }, [municipioIbgeQuery.isSuccess, municipioIbgeQuery.isError, municipioIbgeQuery.data, codigoIbgeAtual]);

  return (
    <div>
      {/* Modal de confirmação para mudanças não salvas */}
      <UnsavedChangesModal
        show={showNavigationModal}
        onConfirm={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />

      {/* Modal para seleção de município */}
      <MunicipioSelector
        show={showMunicipioSelector}
        onHide={() => setShowMunicipioSelector(false)}
        onSelect={handleMunicipioSelect}
      />

      <FormProvider {...form}>
        <PageBreadcrumb items={fornecedorBreadcrumbItems()} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-1">Cadastrar um Fornecedor</h2>
              <h5 className="text-muted fw-normal">
                Preencha as informações para cadastrar um novo fornecedor
              </h5>
              {/* No badge displayed anymore */}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => navigate("/cadastros/fornecedores")}
                className="d-flex align-items-center"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={mutation.isPending}
                className="d-flex align-items-center"
              >
                {mutation.isPending ? (
                  <>
                    <FontAwesomeIcon
                      icon={faCircleNotch}
                      spin
                      className="me-2"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Salvar Fornecedor
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Erros agora são exibidos como toasts */}

          {/* Informações do fornecedor */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faBuilding} className="me-2" />
                Informações do Fornecedor
              </h5>

              {/* Layout com seleção de tipo de pessoa à esquerda e campos à direita */}
              <Row>
                {/* Coluna da esquerda - Seleção de tipo de pessoa */}
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group controlId="tipoPessoa">
                    <Form.Label className="d-block mb-2">
                      Tipo de Pessoa <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex flex-column gap-2">
                      <Card
                        className={`border cursor-pointer ${tipoPessoaSelecionado === TipoPessoa.Juridica
                          ? "border-primary"
                          : "border-light"
                          }`}
                        onClick={() => {
                          setTipoPessoaSelecionado(TipoPessoa.Juridica);
                        }}
                        style={{ transition: "all 0.2s ease-in-out" }}
                      >
                        <Card.Body className="p-2">
                          <div className="d-flex align-items-center">
                            <div
                              className={`d-flex justify-content-center align-items-center rounded-circle me-2`}
                              style={{
                                width: "36px",
                                height: "36px",
                                backgroundColor:
                                  tipoPessoaSelecionado === TipoPessoa.Juridica
                                    ? "#E7F1FF"
                                    : "#f8f9fa",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faBuilding}
                                className={`${tipoPessoaSelecionado === TipoPessoa.Juridica
                                  ? "text-primary"
                                  : "text-secondary"
                                  }`}
                              />
                            </div>
                            <div>
                              <div className="fw-medium">Pessoa Jurídica</div>
                              <div className="small text-muted">
                                Empresas e organizações
                              </div>
                            </div>
                            {tipoPessoaSelecionado === TipoPessoa.Juridica && (
                              <div className="ms-auto">
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  className="text-primary"
                                />
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>

                      <Card
                        className={`border cursor-pointer ${tipoPessoaSelecionado === TipoPessoa.Fisica
                          ? "border-primary"
                          : "border-light"
                          }`}
                        onClick={() => {
                          setTipoPessoaSelecionado(TipoPessoa.Fisica);
                        }}
                        style={{ transition: "all 0.2s ease-in-out" }}
                      >
                        <Card.Body className="p-2">
                          <div className="d-flex align-items-center">
                            <div
                              className={`d-flex justify-content-center align-items-center rounded-circle me-2`}
                              style={{
                                width: "36px",
                                height: "36px",
                                backgroundColor:
                                  tipoPessoaSelecionado === TipoPessoa.Fisica
                                    ? "#E7F1FF"
                                    : "#f8f9fa",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faUser}
                                className={`${tipoPessoaSelecionado === TipoPessoa.Fisica
                                  ? "text-primary"
                                  : "text-secondary"
                                  }`}
                              />
                            </div>
                            <div>
                              <div className="fw-medium">Pessoa Física</div>
                              <div className="small text-muted">Indivíduos</div>
                            </div>
                            {tipoPessoaSelecionado === TipoPessoa.Fisica && (
                              <div className="ms-auto">
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  className="text-primary"
                                />
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                    {/* Campo oculto para manter o valor no formulário */}
                    <Form.Control
                      type="hidden"
                      {...form.register("tipoPessoa")}
                    />
                  </Form.Group>
                </Col>

                {/* Coluna da direita - Campos do formulário */}
                <Col md={9} className="border-start border-light ps-md-4">
                  {/* Seção de campos específicos para Pessoa Jurídica */}
                  <div
                    className="form-section-transition mt-3"
                    style={{
                      opacity:
                        tipoPessoaSelecionado === TipoPessoa.Juridica ? 1 : 0,
                      maxHeight:
                        tipoPessoaSelecionado === TipoPessoa.Juridica
                          ? "1000px"
                          : "0",
                      marginBottom:
                        tipoPessoaSelecionado === TipoPessoa.Juridica
                          ? "1rem"
                          : "0",
                    }}
                  >
                    {tipoPessoaSelecionado === TipoPessoa.Juridica && (
                      <>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="razaoSocial">
                              <Form.Label>
                                Razão Social{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="Razão Social"
                                {...form.register("razaoSocial")}
                                isInvalid={!!form.formState.errors.razaoSocial}
                                className={
                                  fieldsPopulated["razaoSocial"]
                                    ? "highlight-animation"
                                    : ""
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.razaoSocial?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="nomeFantasia">
                              <Form.Label>Nome Fantasia</Form.Label>
                              <Form.Control
                                placeholder="Nome Fantasia"
                                {...form.register("nomeFantasia")}
                                isInvalid={!!form.formState.errors.nomeFantasia}
                                className={
                                  fieldsPopulated["nomeFantasia"]
                                    ? "highlight-animation"
                                    : ""
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.nomeFantasia?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="cnpj">
                              <Form.Label>
                                CNPJ <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="position-relative">
                                <Form.Control
                                  placeholder="00.000.000/0000-00"
                                  {...form.register("cnpj", {
                                    onChange: (e) => {
                                      const formatted = formatCnpj(
                                        e.target.value
                                      );
                                      e.target.value = formatted;
                                    },
                                    onBlur: () => {
                                      // Se o CNPJ tiver 14 dígitos (sem formatação), buscar os dados
                                      const cnpjLimpo =
                                        form
                                          .getValues()
                                          .cnpj?.replace(/\D/g, "") || "";
                                      if (cnpjLimpo.length === 14) {
                                        // Indicar que a consulta foi iniciada pelo usuário
                                        setCnpjConsultaIniciada(true);
                                        dadosEmpresaQuery.refetch();
                                      }
                                    },
                                  })}
                                  isInvalid={!!form.formState.errors.cnpj}
                                />
                                {dadosEmpresaQuery.isPending &&
                                  cnpjConsultaIniciada && (
                                    <div
                                      className="position-absolute"
                                      style={{
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faCircleNotch}
                                        spin
                                        className="text-primary"
                                      />
                                    </div>
                                  )}
                                {showCnpjSuccess && (
                                  <div
                                    className="position-absolute"
                                    style={{
                                      right: "10px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faCheckCircle}
                                      className="text-success"
                                    />
                                  </div>
                                )}
                                <Form.Control.Feedback type="invalid">
                                  {form.formState.errors.cnpj?.message}
                                </Form.Control.Feedback>
                              </div>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="inscricaoEstadual">
                              <Form.Label>Inscrição Estadual</Form.Label>
                              <Form.Control
                                placeholder="Inscrição Estadual"
                                {...form.register("inscricaoEstadual")}
                                isInvalid={
                                  !!form.formState.errors.inscricaoEstadual
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {
                                  form.formState.errors.inscricaoEstadual
                                    ?.message
                                }
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>

                  {/* Seção de campos específicos para Pessoa Física */}
                  <div
                    className="form-section-transition"
                    style={{
                      opacity:
                        tipoPessoaSelecionado === TipoPessoa.Fisica ? 1 : 0,
                      maxHeight:
                        tipoPessoaSelecionado === TipoPessoa.Fisica
                          ? "1000px"
                          : "0",
                      marginBottom:
                        tipoPessoaSelecionado === TipoPessoa.Fisica
                          ? "1rem"
                          : "0",
                    }}
                  >
                    {tipoPessoaSelecionado === TipoPessoa.Fisica && (
                      <>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="nome">
                              <Form.Label>
                                Nome <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="Nome Completo"
                                {...form.register("nome")}
                                isInvalid={!!form.formState.errors.nome}
                                className={
                                  fieldsPopulated["nome"]
                                    ? "highlight-animation"
                                    : ""
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.nome?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="cpf">
                              <Form.Label>
                                CPF <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="000.000.000-00"
                                {...form.register("cpf", {
                                  onChange: (e) => {
                                    const formatted = formatCpf(e.target.value);
                                    e.target.value = formatted;
                                  },
                                })}
                                isInvalid={!!form.formState.errors.cpf}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.cpf?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="dataNascimento">
                              <Form.Label>Data de Nascimento</Form.Label>
                              <Form.Control
                                type="date"
                                {...form.register("dataNascimento")}
                                isInvalid={
                                  !!form.formState.errors.dataNascimento
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.dataNascimento?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>

                  {/* Campos de contato comuns para ambos os tipos */}
                  <div className="mt-4">
                    <div className="border-start border-2 border-primary ps-2 mb-3">
                      <h6 className="text-primary mb-0">
                        Informações de Contato
                      </h6>
                      <p className="text-muted small mb-0">
                        Dados de contato do fornecedor
                      </p>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="telefone">
                          <Form.Label>
                            Telefone <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder="(00) 0000-0000"
                            {...form.register("telefone", {
                              onChange: (e) => {
                                const formatted = formatTelefone(
                                  e.target.value
                                );
                                e.target.value = formatted;
                              },
                            })}
                            className={
                              fieldsPopulated["telefone"]
                                ? "highlight-animation"
                                : ""
                            }
                            isInvalid={!!form.formState.errors.telefone}
                          />
                          <Form.Control.Feedback type="invalid">
                            {form.formState.errors.telefone?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="celular">
                          <Form.Label>Celular</Form.Label>
                          <Form.Control
                            placeholder="(00) 00000-0000"
                            {...form.register("celular", {
                              onChange: (e) => {
                                const formatted = formatTelefone(
                                  e.target.value
                                );
                                e.target.value = formatted;
                              },
                            })}
                            className={
                              fieldsPopulated["celular"]
                                ? "highlight-animation"
                                : ""
                            }
                            isInvalid={!!form.formState.errors.celular}
                          />
                          <Form.Control.Feedback type="invalid">
                            {form.formState.errors.celular?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="email@exemplo.com"
                            {...form.register("email")}
                            className={
                              fieldsPopulated["email"]
                                ? "highlight-animation"
                                : ""
                            }
                            isInvalid={!!form.formState.errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {form.formState.errors.email?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Funções do fornecedor */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                Funções do Fornecedor
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="isFornecedorProduto"
                      label={<span>Fornecedor de Produto</span>}
                      {...form.register("isFornecedorProduto")}
                      defaultChecked={true}
                      className="mb-1"
                    />
                    <div className="text-muted small ps-4">
                      Poderá ser usado em sugestões de compras e pedidos
                    </div>
                  </div>

                  <div className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="isCliente"
                      label={<span>Cliente</span>}
                      {...form.register("isCliente")}
                      className="mb-1"
                    />
                    <div className="text-muted small ps-4">
                      Fornecedor também será registrado como cliente no sistema
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Informações de Compra */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                Informações de Compra
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="prazoEntrega">
                    <Form.Label>Prazo de Entrega (em dias)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      min="0"
                      {...form.register("prazoEntrega", {
                          valueAsNumber: true
                      })}
                      className="mb-1"
                      disabled={!isFornecedorProduto}
                    />
                    <Form.Text className="text-muted">
                      Tempo médio para entrega após aprovação do pedido
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="valorMinimo">
                    <Form.Label>Valor Mínimo para Compra (R$)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0,00"
                      min="0"
                      step="0.01"
                      {...form.register("valorMinimo", {
                          valueAsNumber: true
                      })}
                      className="mb-1"
                      disabled={!isFornecedorProduto}
                    />
                    <Form.Text className="text-muted">
                      Valor mínimo exigido para realização de pedidos
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Endereço do fornecedor */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                Endereço
              </h5>

              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="cep">
                    <Form.Label>
                      CEP <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        placeholder="00000-000"
                        {...form.register("endereco.cep", {
                          required: "CEP é obrigatório",
                          onBlur: (e) => {
                            const cepLimpo = e.target.value.replace(/\D/g, "");
                            if (
                              cepLimpo.length === 8 &&
                              cepLimpo !== lastProcessedCep
                            ) {
                              // Iniciar consulta do CEP
                              setCepConsultaIniciada(true);
                              consultarCep(cepLimpo);
                            }
                          },
                          onChange: (e) => {
                            const formatted = formatCep(e.target.value);
                            e.target.value = formatted;
                          },
                        })}
                        className={
                          fieldsPopulated["endereco.cep"]
                            ? "highlight-animation"
                            : ""
                        }
                        isInvalid={!!form.formState.errors.endereco?.cep}
                      />
                      {dadosCepQuery.isPending && cepConsultaIniciada && (
                        <div
                          className="position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faCircleNotch}
                            spin
                            className="text-primary"
                          />
                        </div>
                      )}
                      {showCepSuccess && (
                        <div
                          className="position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-success"
                          />
                        </div>
                      )}
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.endereco?.cep?.message}
                      </Form.Control.Feedback>
                    </div>
                    <Form.Text className="text-muted">
                      Digite o CEP para autocompletar o endereço
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={8}>
                  <Form.Group controlId="logradouro">
                    <Form.Label>
                      Logradouro <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="Logradouro"
                      {...form.register("endereco.logradouro", {
                        required: "Logradouro é obrigatório",
                      })}
                      className={
                        fieldsPopulated["endereco.logradouro"]
                          ? "highlight-animation"
                          : ""
                      }
                      isInvalid={!!form.formState.errors.endereco?.logradouro}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.logradouro?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="numero">
                    <Form.Label>
                      Número <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="Número"
                      {...form.register("endereco.numero", {
                        required: "Número é obrigatório",
                      })}
                      className={
                        fieldsPopulated["endereco.numero"]
                          ? "highlight-animation"
                          : ""
                      }
                      isInvalid={!!form.formState.errors.endereco?.numero}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.numero?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={12}>
                  <Form.Group controlId="complemento">
                    <Form.Label>Complemento</Form.Label>
                    <Form.Control
                      placeholder="Complemento"
                      {...form.register("endereco.complemento")}
                      className={
                        fieldsPopulated["endereco.complemento"]
                          ? "highlight-animation"
                          : ""
                      }
                      isInvalid={!!form.formState.errors.endereco?.complemento}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.complemento?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={4}>
                  <Form.Group controlId="bairro">
                    <Form.Label>
                      Bairro <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="Bairro"
                      {...form.register("endereco.bairro", {
                        required: "Bairro é obrigatório",
                      })}
                      className={
                        fieldsPopulated["endereco.bairro"]
                          ? "highlight-animation"
                          : ""
                      }
                      isInvalid={!!form.formState.errors.endereco?.bairro}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.bairro?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <Form.Group controlId="municipio">
                    <Form.Label>
                      Cidade <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group input-group-municipio">
                      <Form.Control
                        placeholder="Clique para selecionar município"
                        {...form.register("endereco.municipio", {
                          required: "Cidade é obrigatória",
                        })}
                        className={`${fieldsPopulated["endereco.municipio"]
                          ? "highlight-animation"
                          : ""
                          } rounded-start`}
                        style={{ cursor: "pointer" }}
                        isInvalid={!!form.formState.errors.endereco?.municipio}
                        disabled
                        onClick={() => setShowMunicipioSelector(true)}
                      />
                      <Button
                        variant="primary"
                        onClick={() => setShowMunicipioSelector(true)}
                        title="Buscar município"
                        className="d-flex align-items-center rounded-end"
                        style={{
                          borderTopRightRadius: "0.375rem",
                          borderBottomRightRadius: "0.375rem",
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} className="me-1" />
                        Selecionar
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.endereco?.municipio?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group controlId="uf">
                    <Form.Label>
                      UF <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        placeholder="UF"
                        {...form.register("endereco.uf", {
                          required: "UF é obrigatória",
                        })}
                        className={`${fieldsPopulated["endereco.uf"]
                          ? "highlight-animation"
                          : ""
                          }`}
                        style={{ cursor: "pointer" }}
                        isInvalid={!!form.formState.errors.endereco?.uf}
                        disabled
                        onClick={() => setShowMunicipioSelector(true)}
                      />
                      {form.watch("endereco.uf") && (
                        <span
                          className="position-absolute end-0 top-0 d-flex align-items-center h-100 px-3 text-muted"
                          style={{ pointerEvents: "none" }}
                        >
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </span>
                      )}
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.endereco?.uf?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Campo oculto para o ID do município */}
              <Form.Control type="hidden" {...form.register("endereco.municipioId")} />
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/cadastros/fornecedores")}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {mutation.isPending ? "Salvando..." : "Salvar Fornecedor"}
            </Button>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovoFornecedor;
